import React from 'react';
import { styles, colors } from '../styles/styles';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Header component with user controls and brand name
 * Hidden on detail pages
 */
const Header = ({ user, onLogin, onLogout }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Hide header on detail pages
    const isDetailPage = location.pathname.includes('/thought/') ||
                         location.pathname.includes('/edit/') ||
                         location.pathname.includes('/edit-field/') ||
                         location.pathname.includes('/compose') ||
                         location.pathname.includes('/collection/');

    // Get current route for active button styling
    const isRoute = (path) => location.pathname === path;

    if (isDetailPage) {
        return null; // Don't render header on detail pages
    }
    
    const baseButtonStyle = {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
        transition: 'background-color 200ms ease',
    };
    
    return (
        <header style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 24px',
            paddingLeft: '24px', // Consistent padding
            marginBottom: '16px',
            backgroundColor: colors.background,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 25,
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        }}>
            {/* Left side buttons container */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                {/* Home button */}
                <button
                    onClick={() => navigate('/')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Home"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 22V12h6v10" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Deck button — flashcard review of high-importance thoughts */}
                <button
                    onClick={() => navigate('/deck')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/deck') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Deck"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="6" width="13" height="15" rx="2" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 3h9a2 2 0 0 1 2 2v11" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Timeline button */}
                <button
                    onClick={() => navigate('/timeline')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/timeline') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Timeline"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="9" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 7V12L15 15" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Reflect button — search your saved thoughts by feeling */}
                <button
                    onClick={() => navigate('/reflect')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/reflect') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Reflect"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
                            <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
                            <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>
                            <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/>
                            <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/>
                            <path d="M3.477 10.896a4 4 0 0 1 .585-.396"/>
                            <path d="M19.938 10.5a4 4 0 0 1 .585.396"/>
                            <path d="M6 18a4 4 0 0 1-1.967-.516"/>
                            <path d="M19.967 17.484A4 4 0 0 1 18 18"/>
                        </g>
                    </svg>
                </button>

                {/* Search button — direct fuzzy keyword search over all thoughts */}
                <button
                    onClick={() => navigate('/search')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/search') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Search"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="11" cy="11" r="7" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M20.5 20.5L16 16" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

            </div>
            
            {/* iOS animation style */}
            <style>
                {`
                    @keyframes ios-tap-pulse {
                        0% { opacity: 1; transform: scale(1); }
                        35% { opacity: 0.4; transform: scale(0.97); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    
                    .ios-button:active {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }
                    
                    .ios-tapped {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }
                `}
            </style>
            
            {/* User controls */}
            {!user ? (
                <button
                    onClick={onLogin}
                    style={{
                        ...styles.button,
                        ...styles.whiteButton,
                        padding: '6px 16px',
                        fontSize: '14px',
                    }}
                >
                    Sign in
                </button>
            ) : (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    {/* User initial in circle */}
                    <div style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: colors.textSecondary,
                        fontWeight: '500',
                        fontSize: '12px',
                        border: `1.5px solid ${colors.textSecondary}`,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }} title={user.displayName}>
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}
                    </div>
                
                    <button
                        onClick={onLogout}
                        className="ios-button"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            color: colors.textSecondary,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '8px',
                            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                        }}
                        title="Sign out"
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 17L21 12L16 7" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M21 12H9" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            )}
        </header>
    );
};

export default Header;